<template>
  <div :id="`${source}_bulk_assign_tasks`">
    <a-button
      @click="handleAddTaskClick"
      class="secondary_small gap-1 flex items-center justify-center"
    >
      <ThemeImage
        class="size-4"
        :icon="imageNames.ADD_TASK_GREY"
        alt="add_task"
      />
      <span>Add CRM Task</span>
    </a-button>

    <AddDynamicEntity
      v-if="isTasksDrawerVisible"
      :visible="isTasksDrawerVisible"
      slug="crm_tasks"
      entity-display-name="CRM Task"
      :object-id="entityTypes.CRM_TASKS"
      :properties="taskProperties"
      :sub-objects="taskSubObjects"
      :initial-data="taskDefaultValues"
      :bulk-create-mode="true"
      :bulk-create-data="bulkCreateData"
      @update:visible="isTasksDrawerVisible = $event"
      @entity-saved="handleTaskAdded"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import AddDynamicEntity from "@/src/views/dynamic_crm/AddDynamicEntity.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { pluralizeEntityType } from "@/src/constants/crmConstants.js";
import { getRelationshipSlugs } from "@/src/utils/relationshipUtils.js";

const props = defineProps({
  source: { type: String, required: true },
  selection: { type: Object, required: true },
  selectCount: { type: Number, required: true },
  viewId: { type: Number, default: null },
  listId: { type: Number, default: null },
});

const emit = defineEmits(["bulk-assigned-tasks"]);

const store = useStore();
const isTasksDrawerVisible = ref(false);
const taskProperties = ref([]);
const taskSubObjects = ref([]);
const relationshipId = ref(null);

const taskDefaultValues = computed(() => ({
  status: "Todo",
  priority: "Medium",
}));

const bulkCreateData = computed(() => ({
  target_object_type: pluralizeEntityType(props.source),
  relationship_id: relationshipId.value,
  selected_ids: props.selection.selected_ids || [],
  unselected_ids: props.selection.unselected_ids || [],
  select_all: props.selection.select_all || false,
  view_id: props.viewId,
}));

const fetchRelationshipId = async () => {
  try {
    const sourceSlug = pluralizeEntityType(props.source);

    let sourceSchema = store.getters["schema/getSchemaBySlug"](sourceSlug);

    if (!sourceSchema) {
      const result = await store.dispatch("schema/fetchSchema", {
        slug: sourceSlug,
      });

      if (result && result.success && result.schema) {
        sourceSchema = result.schema;
      }
    }

    if (sourceSchema && sourceSchema.relationships) {
      const taskRelationship = sourceSchema.relationships.find((rel) => {
        const { sourceSlug: relSource, targetSlug: relTarget } =
          getRelationshipSlugs(rel);
        return (
          (relSource === sourceSlug && relTarget === "crm_tasks") ||
          (relSource === "crm_tasks" && relTarget === sourceSlug)
        );
      });

      if (taskRelationship) {
        relationshipId.value = taskRelationship.id;
      }
    }
  } catch (error) {
    console.error("Error fetching relationship ID:", error);
  }
};

const fetchTaskSchema = async () => {
  try {
    let existingSchema = store.getters["schema/getSchemaBySlug"]("crm_tasks");

    if (existingSchema) {
      taskProperties.value = existingSchema.attributes || [];
      taskSubObjects.value = existingSchema.sub_objects || [];
      return;
    }

    const result = await store.dispatch("schema/fetchSchema", {
      slug: "crm_tasks",
    });

    if (result && result.success && result.schema) {
      taskProperties.value = result.schema.attributes || [];
      taskSubObjects.value = result.schema.sub_objects || [];
    }
  } catch (error) {
    console.error("Error fetching CRM Tasks schema:", error);
  }
};

const handleAddTaskClick = async () => {
  await Promise.all([fetchTaskSchema(), fetchRelationshipId()]);
  isTasksDrawerVisible.value = true;
};

const handleTaskAdded = () => {
  isTasksDrawerVisible.value = false;
  emit("bulk-assigned-tasks");
};
</script>
